import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../store'
import { setNoteGroups, setNoteGroupsLoading } from '../store/slices/noteGroupSlice'
import type { NoteGroup } from '../store/slices/noteGroupSlice'
import { getNoteGroupsCollection } from '../store/collections/noteGroupsCollection'

let globalSubscription: { unsubscribe: () => void } | null = null
let globalIsInitialized = false

// 最近插入保护：防止 subscribeChanges 在 Shape 流推送前误删新记录
const recentlyInsertedIds = new Set<string>()
const recentlyInsertedData = new Map<string, NoteGroup>()
const RECENT_INSERT_TTL = 5000

export function markRecentlyInserted(id: string, group: NoteGroup) {
  recentlyInsertedIds.add(id)
  recentlyInsertedData.set(id, group)
  setTimeout(() => {
    recentlyInsertedIds.delete(id)
    recentlyInsertedData.delete(id)
  }, RECENT_INSERT_TTL)
}

function deserializeNoteGroup(raw: any): NoteGroup {
  let noteIds: string[] = []
  const rawNoteIds = raw.note_ids ?? raw.noteIds
  if (Array.isArray(rawNoteIds)) {
    noteIds = rawNoteIds
  } else if (typeof rawNoteIds === 'string') {
    try {
      const parsed = JSON.parse(rawNoteIds)
      noteIds = Array.isArray(parsed) ? parsed : []
    } catch {
      noteIds = []
    }
  }

  const rawCreatedAt = raw.created_at ?? raw.createdAt
  const rawUpdatedAt = raw.updated_at ?? raw.updatedAt

  return {
    id: raw.id,
    userId: raw.user_id ?? raw.userId ?? '',
    name: raw.name || '',
    noteIds,
    source: (raw.source === 'rightEditor' ? 'rightEditor' : 'editor') as 'editor' | 'rightEditor',
    createdAt: rawCreatedAt instanceof Date ? rawCreatedAt.toISOString() : (rawCreatedAt || ''),
    updatedAt: rawUpdatedAt instanceof Date ? rawUpdatedAt.toISOString() : (rawUpdatedAt || ''),
  }
}

// 导出供外部手动触发同步 — 从 collection.toArray 读取
export function syncNoteGroupsToRedux(dispatch: any, currentGroups?: NoteGroup[]) {
  try {
    const collection = getNoteGroupsCollection()
    const rawGroups = collection.toArray || []
    console.log('[NoteGroups] 手动同步: toArray 返回', rawGroups.length, '条记录')
    let groups: NoteGroup[] = rawGroups.map(deserializeNoteGroup)

    // 保留最近插入但 Shape 流尚未推送的记录
    if (recentlyInsertedIds.size > 0) {
      for (const id of recentlyInsertedIds) {
        if (!groups.find(g => g.id === id)) {
          const data = recentlyInsertedData.get(id)
          if (data) {
            console.log('[NoteGroups] 保留最近插入记录:', id)
            groups.push(data)
          }
        }
      }
    }

    dispatch(setNoteGroups(groups))
    dispatch(setNoteGroupsLoading(false))
  } catch (err) {
    console.warn('[NoteGroups] 手动同步失败:', err)
  }
}

export function useSyncNoteGroupsToRedux() {
  const dispatch = useAppDispatch()
  const { groups: currentGroups } = useAppSelector((state: any) => state.noteGroups)
  const [isReady, setIsReady] = useState(false)
  const isInitializedRef = useRef(false)
  const currentGroupsRef = useRef(currentGroups)

  useEffect(() => {
    currentGroupsRef.current = currentGroups
  }, [currentGroups])

  const syncFromCollection = () => {
    try {
      const collection = getNoteGroupsCollection()
      const rawGroups = collection.toArray || []
      console.log('[NoteGroups] subscribeChanges 同步: toArray 返回', rawGroups.length, '条记录')
      let groups: NoteGroup[] = rawGroups.map(deserializeNoteGroup)

      // 保留最近插入但 Shape 流尚未推送的记录
      if (recentlyInsertedIds.size > 0) {
        for (const id of recentlyInsertedIds) {
          if (!groups.find(g => g.id === id)) {
            const data = recentlyInsertedData.get(id)
            if (data) {
              console.log('[NoteGroups] 保留最近插入记录:', id)
              groups.push(data)
            }
          }
        }
      }

      dispatch(setNoteGroups(groups))
      dispatch(setNoteGroupsLoading(false))

      if (!isReady) {
        setIsReady(true)
      }
    } catch (err) {
      console.error('[NoteGroups] syncFromCollection 失败:', err)
    }
  }

  useEffect(() => {
    if (globalIsInitialized || isInitializedRef.current) {
      return
    }

    isInitializedRef.current = true
    globalIsInitialized = true
    dispatch(setNoteGroupsLoading(true))

    let noteGroupsWarnOnce = false

    const setupSubscription = () => {
      try {
        const collection = getNoteGroupsCollection()

        if (!globalSubscription) {
          console.log('[NoteGroups] 设置 subscribeChanges 订阅')
          const subscription = collection.subscribeChanges((changes: any[]) => {
            console.log('[NoteGroups] subscribeChanges 触发, changes:', changes?.length ?? 0)
            syncFromCollection()
          }, { includeInitialState: true })

          globalSubscription = subscription
          syncFromCollection()

          return () => {
            subscription.unsubscribe()
            globalSubscription = null
            globalIsInitialized = false
            isInitializedRef.current = false
          }
        }
      } catch (err) {
        // 首次用 warn 保留诊断信息，后续轮询用 debug 避免日志刷屏
        if (!noteGroupsWarnOnce) {
          noteGroupsWarnOnce = true
          console.warn('[NoteGroups] setupSubscription 等待集合初始化（后续轮询静默）:', err)
        }
      }
      return undefined
    }

    let cleanup: (() => void) | undefined
    const checkInterval = setInterval(() => {
      const result = setupSubscription()
      if (result) {
        cleanup = result
        clearInterval(checkInterval)
      }
    }, 500)

    return () => {
      clearInterval(checkInterval)
      if (cleanup) {
        cleanup()
      }
      isInitializedRef.current = false
      globalIsInitialized = false
    }
  }, [dispatch])

  return {
    isReady,
  }
}
